import { get, post } from "@selector/shared";
import { type components, type GenomicFeatureTypeEnum, type SyntenyTypeEnum } from "@selector/shared/openapi";

import type { RangePerContig } from "../store/slices/selection-slice.ts";
import type { SessionRequest } from "../types.ts";

export function getAlignedContigs(syntenyOrgId: number, orgId: number, syntenyType: SyntenyTypeEnum) {
  return get("/synteny/{syntenyOrgId}/componentGenome/{orgId}/{syntenyType}/alignedContigs", {
    syntenyOrgId,
    orgId,
    syntenyType,
  });
}

export function fetchSyntenyInfo(orgId: number) {
  return get("/genome/{orgId}/info", { orgId });
}

export function fetchComponentGenomes(syntenyOrgId: number) {
  return get("/genome/componentGenomes/{orgId}", { orgId: syntenyOrgId });
}

export type SyntenyFamilyContigs = {
  contigs: components["schemas"]["SyntenyFamilyContigResponse"][];
  organismId: number;
  organismName: string;
}[];

export function fetchFeaturesFamilyMembersOrThrow(familyId: number, condense?: boolean): Promise<SyntenyFamilyContigs> {
  return get("/synteny/proteinFamily/{familyId}/members", { familyId }, { condense });
}

export function fetchPangenomeAlignmentMembersOrThrow(
  pangenomeOrgId: number,
  familyId: number,
): Promise<SyntenyFamilyContigs> {
  return get("/synteny/{pangenomeOrgId}/pangenomeAlignment/{familyId}/members", { pangenomeOrgId, familyId });
}

export function fetchPangenomeAlignmentMembersByFeatureOrThrow(
  pangenomeOrgId: number,
  featureType: GenomicFeatureTypeEnum,
  featureId: number,
): Promise<SyntenyFamilyContigs> {
  return get("/synteny/{pangenomeOrgId}/pangenomeAlignment/{featureType}/{featureId}/members", {
    pangenomeOrgId,
    featureType,
    featureId,
  });
}

export function fetchFamilyMembersCondensedOrThrow(
  syntenyOrgId: number,
  syntenyType: SyntenyTypeEnum,
  featureId: number,
): Promise<SyntenyFamilyContigs> {
  const endpoint = "/synteny/{syntenyOrgId}/{syntenyType}/feature/{featureId}/syntenyMembers";
  return get(endpoint, { syntenyOrgId, syntenyType, featureId }, { condense: true });
}

export function getFeatureTypes(orgId: number) {
  return get("/genome/{orgId}/featureTypes", { orgId });
}

export function fetchFeatureInfo(type: GenomicFeatureTypeEnum, featureId: number) {
  return get("/feature/{type}/{featureId}/info", { type, featureId });
}

export function fetchPangenomeInfo(pangenomeOrgId: number, featureId: number) {
  return get("/feature/pangenome/{pangenomeOrgId}/{featureId}/info", { pangenomeOrgId, featureId });
}

export async function getSequenceViewerSessionIdFromRanges(ranges: RangePerContig[]): Promise<string> {
  const { data } = await post("/sequenceAnalyzer/createSessionFromRanges", undefined, undefined, { ranges }, "text");
  return data;
}

export async function getSequenceViewerSessionIdFromFeatures(featuresGroup: SessionRequest): Promise<string> {
  const { data } = await post("/sequenceAnalyzer/createSession", undefined, undefined, { ...featuresGroup }, "text");
  return data;
}

export type SearchType = components["schemas"]["SearchGenomicEntityTypeRequest"];

export function search(genomeId: number, searchTerm: string, types: SearchType[], signal: AbortSignal) {
  return post(
    "/search/genomicFeature",
    undefined,
    undefined,
    { genomeId, limit: 5, searchTerm, types },
    "json",
    signal,
  );
}

export function fetchFeatureData(resultType: GenomicFeatureTypeEnum, featureId: number) {
  return get("/feature/{type}/{featureId}/locations", { type: resultType, featureId });
}
